/**
 * One policy for a Company portal's 503 SESSION_REFRESHING (ADR-024).
 *
 * A portal holds only an opaque session handle; the SERVER refreshes the
 * tokens behind it. A request that lands while that refresh is running — or
 * while it is failing transiently — answers 503 SESSION_REFRESHING with a
 * Retry-After. The session is live and the request is worth repeating, so
 * nothing here ever reads the answer as "signed out": only the server saying
 * 401 or 403 ends a portal session (see `auth-refresh.ts`).
 *
 * Three callers wait this way — the JSON transport in `api.ts`, the portal
 * revalidation in `auth-refresh.ts` and the boot probe in `auth-api.ts` — and
 * differ only in how many times they are prepared to ask
 * again. They all take the wait from here, parameterised by that count, so
 * the delay rule and the "is this a refresh?" test cannot drift apart.
 *
 * Dependency-free on purpose: `api.ts`, `auth-refresh.ts` and `auth-api.ts`
 * already import one another, and the transport tests replace `auth-refresh`
 * wholesale — a module that imports nothing can be shared by all of them and
 * is never mocked away from the code under test.
 */

export const SESSION_REFRESHING_CODE = 'SESSION_REFRESHING';

/** A decoded error envelope says the portal session is mid-refresh. */
export function isSessionRefreshingBody(status: number, body: unknown): boolean {
  return (
    status === 503 &&
    (body as { error?: { code?: string } } | null)?.error?.code === SESSION_REFRESHING_CODE
  );
}

/**
 * Whether a response is a Company portal's 503 SESSION_REFRESHING: the session
 * is live and mid-refresh on the server, so the request is worth repeating.
 * Reads a clone, so the caller can still consume the body.
 */
export async function isSessionRefreshing(response: Response): Promise<boolean> {
  if (response.status !== 503) return false;
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  return isSessionRefreshingBody(response.status, body);
}

const MIN_DELAY_MS = 500;
const MAX_DELAY_MS = 5000;
const DEFAULT_DELAY_MS = 1000;

/** A wait in ms bounded to what a UI can sensibly hold: 0.5–5 s. */
function boundedDelayMs(ms: number): number {
  return Number.isFinite(ms) && ms > 0
    ? Math.min(Math.max(ms, MIN_DELAY_MS), MAX_DELAY_MS)
    : DEFAULT_DELAY_MS;
}

/** The server's Retry-After for SESSION_REFRESHING, bounded to 0.5–5 s. */
export function sessionRefreshingDelayMs(response: Response): number {
  const seconds = Number(response.headers.get('retry-after'));
  return boundedDelayMs(seconds * 1000);
}

/**
 * Sends a request and, while the answer is SESSION_REFRESHING, waits out the
 * server's Retry-After and sends it again — at most `maxRetries` more times.
 * Resolves with the LAST response, which may still be the 503: deciding what
 * an exhausted wait means is the caller's (the transport throws it as the
 * request's error; the boot probe keeps the session and asks again later).
 * An aborted signal stops the waiting; the caller's own abort handling sees
 * the response it already has.
 */
export async function retryWhileSessionRefreshing(
  send: () => Promise<Response>,
  options: { maxRetries: number; signal?: AbortSignal },
): Promise<Response> {
  let response = await send();
  for (let attempt = 0; attempt < options.maxRetries; attempt += 1) {
    if (options.signal?.aborted || !(await isSessionRefreshing(response))) break;
    await new Promise<void>((resolve) => {
      const timer = setTimeout(done, sessionRefreshingDelayMs(response));
      // Abandoned during the wait: stop waiting at once.
      function done() {
        clearTimeout(timer);
        options.signal?.removeEventListener('abort', done);
        resolve();
      }
      options.signal?.addEventListener('abort', done, { once: true });
    });
    if (options.signal?.aborted) break;
    response = await send();
  }
  return response;
}

/**
 * The error a caller throws when the wait above was exhausted (the auth
 * client's `AuthFetchError` carries the server's `code`). Duck-typed on the
 * code so the boot sequence can recognise it without importing the auth
 * client — the one place that must not read it as "signed out".
 */
export function isSessionRefreshingError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === SESSION_REFRESHING_CODE
  );
}

/**
 * How long to wait before asking again after such an error: the server's
 * Retry-After, carried on the error as the instant it may be retried
 * (`retryAt`), bounded like a response's; the default when it has none.
 */
export function sessionRefreshingRetryDelayMs(err: unknown, now = Date.now()): number {
  const retryAt = (err as { retryAt?: unknown } | null)?.retryAt;
  if (typeof retryAt !== 'string') return DEFAULT_DELAY_MS;
  return boundedDelayMs(Date.parse(retryAt) - now);
}
